import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Clock, AlertTriangle, Ban, ChefHat, Wine, Flame, UtensilsCrossed, RefreshCw, ClipboardPlus, Pencil, HandPlatter, Receipt, CreditCard, Wallet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReceiptPrint from '../../components/ReceiptPrint';
import AlertPrefsButton from '../../components/AlertPrefsButton';
import OrderEditDialog from '../../components/OrderEditDialog';
import OrderDiscountButton from '../../components/OrderDiscountButton';
import PendingTabsTray from '../../components/PendingTabsTray';
import CashDrawerCard from '../../components/CashDrawerCard';
import PartialPaymentsPanel from '../../components/PartialPaymentsPanel';
import SettledTabGroupCard from '../../components/SettledTabGroupCard';
import PostSettleTablePrompt from '../../components/PostSettleTablePrompt';
import StaleTabsAlert from '../../components/StaleTabsAlert';
import { useAuth } from '../../contexts/AuthContext';
import { useOrderAlert } from '../../lib/orderAlert';
import { tableLabel } from '../../lib/tableLabel';

const delayThresholds = { food: 10, drinks: 5, shisha: 10 };
function getElapsedMinutes(ts) { return Math.floor((Date.now() - new Date(ts).getTime()) / 60000); }

export default function OrdersPage() {
  const { user } = useAuth();
  const canDiscount = ['manager', 'director'].includes(user?.role);
  // Heal Stale Data button: visible to both manager + director (owner ask
  // Feb 2026 — "give heal stale data button to manager panel as well").
  const canHeal = canDiscount;
  const [orders, setOrders] = useState([]);
  const [voidDialog, setVoidDialog] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [syncing, setSyncing] = useState(false);
  // Active view layout: 'tables' groups orders by table (manager's bird's-
  // eye floor view — the kiosk default) or 'list' shows every order as
  // its own card. Persisted to localStorage so the manager's preference
  // sticks between sessions.
  const [activeLayout, setActiveLayout] = useState(() => {
    try { return localStorage.getItem('majnu:orders:layout') || 'tables'; } catch { return 'tables'; }
  });
  useEffect(() => {
    try { localStorage.setItem('majnu:orders:layout', activeLayout); } catch { /* sandboxed */ }
  }, [activeLayout]);
  // 2026-02 owner request: every table's order window should be OPEN by
  // default on the live orders page so staff can see at-a-glance what's
  // served vs. pending. We track the *collapsed* set instead of a single
  // expanded one — empty set = all open, which is the desired default.
  // Persisted to localStorage so the manager's preference survives a page
  // refresh.
  const [collapsedTables, setCollapsedTables] = useState(() => {
    try {
      const raw = localStorage.getItem('majnu:orders:collapsedTables');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const toggleTableCollapsed = (tableId) => {
    setCollapsedTables(prev => {
      const next = new Set(prev);
      if (next.has(tableId)) next.delete(tableId); else next.add(tableId);
      try { localStorage.setItem('majnu:orders:collapsedTables', JSON.stringify(Array.from(next))); } catch { /* sandboxed */ }
      return next;
    });
  };

  // 'active' = live/preparing/ready orders (default, polled every 5s)
  // 'completed' = served+closed orders (polled every 30s — managers use this
  // tab to look at the day's history, no need to thrash the server).
  const [view, setView] = useState('active');
  // Completed-view date filter. Empty strings = backend default (last 48h:
  // today + yesterday). When the manager picks any date(s) we send them as
  // query params and the backend swaps to the explicit window. YYYY-MM-DD.
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Manager order-on-behalf — table dropdown dialog
  const [takeOrderOpen, setTakeOrderOpen] = useState(false);
  const [allTables, setAllTables] = useState([]);
  const [pickedTableQr, setPickedTableQr] = useState('');
  const [editOrder, setEditOrder] = useState(null);
  // Tab settle dialog: holds the preview data (sum_total, owed_at_counter,
  // tab + sibling orders) when a manager clicks Settle on a tab order.
  const [settlePreview, setSettlePreview] = useState(null);
  // After a tab is settled, prompt staff whether to free the table or
  // keep it occupied (owner request May 31 2026 — staff don't always
  // want to clear the table the instant payment lands).
  const [postSettlePromptTable, setPostSettlePromptTable] = useState(null);
  const [settling, setSettling] = useState(false);
  const [showPartials, setShowPartials] = useState(false);
  const [splitOrderId, setSplitOrderId] = useState(null);  // direct-order partials dialog
  const navigate = useNavigate();

  const [fetchError, setFetchError] = useState('');

  const fetchOrders = useCallback(async () => {
    try {
      let q;
      if (view === 'completed') {
        const params = new URLSearchParams({ status: 'completed' });
        if (fromDate) params.set('from_date', fromDate);
        if (toDate) params.set('to_date', toDate);
        q = `/orders?${params.toString()}`;
      } else {
        q = '/orders?status=active';
      }
      const { data } = await api.get(q);
      setOrders(data);
      setFetchError('');
    } catch (err) {
      if (err?.response?.status === 401) setFetchError('Session expired — please log in again');
      else if (!navigator.onLine) setFetchError('No internet connection');
      else setFetchError('Connection issue — retrying…');
    }
  }, [view, fromDate, toDate]);

  // PWA-safe polling: also refetch on tab visibility / window focus / coming back online.
  useEffect(() => {
    fetchOrders();
    const interval = view === 'completed' ? 30000 : 5000;
    const i = setInterval(fetchOrders, interval);
    const onVisibility = () => { if (document.visibilityState === 'visible') fetchOrders(); };
    const onFocus = () => fetchOrders();
    const onOnline = () => fetchOrders();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    return () => {
      clearInterval(i);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, [fetchOrders, view]);

  const syncPayments = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/payments/reconcile');
      const { checked = 0, fixed = 0, voided = 0 } = data || {};
      if (fixed === 0 && voided === 0) {
        toast.success(`All ${checked} pending order${checked === 1 ? '' : 's'} are up to date`);
      } else {
        toast.success(`Synced · ${fixed} confirmed paid · ${voided} auto-voided`);
      }
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // `approveCash` removed Feb 2026 — the pending-cash flow (customer
  // picks "Pay Cash At Bar" → bartender confirms cash with this button)
  // was retired by the owner. Backend endpoint `/payments/cash-received`
  // is intentionally LEFT in place so historical rows can still be
  // re-confirmed via API if ever needed.

  // Manager / director / bartender already took payment by card on the EFTPOS
  // terminal (or a separate device) — this closes the sale in our books and
  // logs a `card_manual` payment_transactions row.
  const acceptUberOrder = async (orderId) => {
    try {
      await api.post(`/uber/orders/${orderId}/accept`, { ready_in_minutes: 20 });
      toast.success('Uber Eats order accepted');
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to accept'); }
  };

  const denyUberOrder = async (orderId) => {
    const reason = window.prompt('Reject this Uber Eats order?\n\nReason (shown to Uber):', 'Store cannot fulfil this order');
    if (reason === null) return;
    try {
      await api.post(`/uber/orders/${orderId}/deny`, { reason });
      toast.success('Uber Eats order rejected');
      fetchOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reject'); }
  };

  const markPaidByCard = async (orderId) => {
    const reference = window.prompt(
      'Mark this order as PAID by card?\n\n(Optional) Enter the terminal slip / auth code — leave blank to skip:',
      ''
    );
    if (reference === null) return; // user cancelled
    try {
      await api.post(`/payments/manual-card-paid/${orderId}`, { reference: (reference || '').trim() });
      toast.success('Card payment recorded · order closed');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark paid');
    }
  };

  // Tab settlement: fetch the live tab preview (sum of all tab orders − $30)
  // and open the confirm dialog. The actual settle happens on confirm.
  const openSettleTab = async (orderId) => {
    try {
      const { data } = await api.get(`/tabs/by-order/${orderId}`);
      // Carry the calling order's table info forward — the post-settle
      // "Mark Available / Keep Occupied" prompt needs the table_id and
      // we don't want a second roundtrip just to look it up.
      const callingOrder = orders.find(o => o.id === orderId);
      setSettlePreview({
        order_id: orderId,
        ...data,
        _table: callingOrder?.table_id
          ? { id: callingOrder.table_id, number: callingOrder.table_number }
          : null,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load tab');
    }
  };

  // One-shot repair tool for legacy data — finds orders that were
  // prematurely marked completed by the old auto-flip (when items were
  // served but payment wasn't yet) and moves them back to active so the
  // kiosk Live Orders view picks them up. Idempotent.
  const [reactivating, setReactivating] = useState(false);
  const reactivateUnpaid = async () => {
    if (reactivating) return;
    const ok = window.confirm("Move every 'completed' order whose payment isn't fully captured (still on tab / pending cash) back to Active?\n\nSafe to run anytime — orders that ARE properly paid won't be touched.");
    if (!ok) return;
    setReactivating(true);
    try {
      const { data } = await api.post('/admin/reactivate-unpaid-completed-orders');
      if (data.reactivated === 0) {
        toast.info('Nothing to move — everything in Completed is properly paid.');
      } else {
        toast.success(`Reactivated ${data.reactivated} order${data.reactivated === 1 ? '' : 's'} — refreshing…`);
        await fetchOrders();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reactivate failed');
    } finally {
      setReactivating(false);
    }
  };

  const confirmSettleTab = async (settlementMethod = null) => {
    if (!settlePreview) return;
    setSettling(true);
    try {
      // Pass settlement_method as a query param so the backend can apply
      // the Cash Pay On Counter — 10% Off promo when both the tab opening
      // AND the final settle are cash. Card/Apple Pay settle forfeits the
      // discount even on a cash-opened tab.
      const url = settlementMethod
        ? `/tabs/by-order/${settlePreview.order_id}/settle?settlement_method=${encodeURIComponent(settlementMethod)}`
        : `/tabs/by-order/${settlePreview.order_id}/settle`;
      const { data } = await api.post(url);
      const owed = Number(data.owed_at_counter || 0).toFixed(2);
      const count = data.orders_settled;
      const discount = Number(data.discount_amount || 0);
      const methodLabel = settlementMethod === 'cash' ? 'cash' : settlementMethod === 'card' ? 'card' : 'at bar';
      if (discount > 0) {
        toast.success(`Tab closed · 10% Off applied (−$${discount.toFixed(2)}) · collect $${owed} ${methodLabel} · ${count} order${count === 1 ? '' : 's'} marked paid`);
      } else if (Number(data.owed_at_counter) <= 0) {
        toast.success(`Tab closed · ${count} order${count === 1 ? '' : 's'} marked paid · $30 deposit covered the bill`);
      } else {
        toast.success(`Tab closed · collect $${owed} ${methodLabel} · ${count} order${count === 1 ? '' : 's'} marked paid`);
      }
      // Stash the table info so we can open the post-settle prompt AFTER
      // closing the settle dialog (otherwise two dialogs would stack).
      const tableForPrompt = settlePreview._table;
      setSettlePreview(null);
      fetchOrders();
      if (tableForPrompt?.id) setPostSettlePromptTable(tableForPrompt);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Settle failed');
    } finally {
      setSettling(false);
    }
  };

  const voidOrder = async () => {
    if (!voidReason.trim()) { toast.error('Reason required'); return; }
    try { await api.put(`/orders/${voidDialog}/void`, { reason: voidReason }); toast.success('Order voided'); setVoidDialog(null); setVoidReason(''); fetchOrders(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // ---- Void entire tab (manager / director god-mode) ----
  // Owner ask Feb 2026: "Give admin all options to edit or void any item or
  // any order or any table". `voidTabDialog` holds { tabId, customerName,
  // tableNumber } so the confirm dialog can show what's about to be wiped.
  const [voidTabDialog, setVoidTabDialog] = useState(null);
  const [voidTabReason, setVoidTabReason] = useState('');
  // Owner ask Feb 2026: "Its still here on manager and admin panel, why?"
  // Pre-fix auto-day-close ghosts + voided ghost tabs need a one-click
  // cleanup so production fixes don't require SSH. This button POSTs to
  // `/api/admin/heal-stale-data` and shows the count of items cleaned.
  const [healing, setHealing] = useState(false);
  const healStaleData = async () => {
    if (healing) return;
    if (!window.confirm('Heal stale ghost data?\n\nThis will:\n• Mark orders linked to auto-day-closed tabs as completed (so they leave the Active list)\n• Auto-close ghost tabs (every order voided, no money)\n• Free tables that have no active orders / tabs\n\nSafe to run anytime.')) return;
    setHealing(true);
    try {
      const { data } = await api.post('/admin/heal-stale-data');
      toast.success(
        `Healed · ${data.ghost_orders_healed} ghost orders · ${data.ghost_tabs_closed} ghost tabs · ${data.tables_freed} tables freed`,
        { duration: 10000 }
      );
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Heal failed');
    } finally {
      setHealing(false);
    }
  };

  const voidTab = async () => {
    if (!voidTabDialog) return;
    const reason = voidTabReason.trim();
    if (reason.length < 3) { toast.error('Reason required (min 3 chars)'); return; }
    try {
      const { data } = await api.post(`/tabs/${voidTabDialog.tabId}/void`, { reason });
      const refundBit = data.refund_required_amount > 0
        ? ` · Refund $${Number(data.refund_required_amount).toFixed(2)} due to customer`
        : '';
      const tableBit = data.table_freed ? ' · Table freed' : '';
      toast.success(`Tab voided · ${data.voided_orders} order${data.voided_orders === 1 ? '' : 's'}${refundBit}${tableBit}`, { duration: 8000 });
      setVoidTabDialog(null);
      setVoidTabReason('');
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Void tab failed');
    }
  };

  const openTakeOrder = async () => {
    try {
      const { data } = await api.get('/tables');
      setAllTables((data || []).sort((a, b) => a.number - b.number));
      setPickedTableQr('');
      setTakeOrderOpen(true);
    } catch (err) {
      toast.error('Could not load tables');
    }
  };

  // While the dialog is open, refetch tables every 5s so the dropdown
  // reflects auto-occupy / clear changes in near-real-time (single source
  // of truth lives on the backend).
  useEffect(() => {
    if (!takeOrderOpen) return undefined;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get('/tables');
        setAllTables((data || []).sort((a, b) => a.number - b.number));
      } catch { /* ignored */ }
    }, 5000);
    return () => clearInterval(id);
  }, [takeOrderOpen]);

  const launchStaffOrder = () => {
    if (!pickedTableQr) { toast.error('Pick a table'); return; }
    // Open the customer page in a new tab with ?staff=1 so CustomerPage
    // skips the auth gate and defaults payment to cash. Manager submits the
    // order from the same QR experience the customer would see.
    window.open(`/table/${pickedTableQr}?staff=1`, '_blank', 'noopener,noreferrer');
    setTakeOrderOpen(false);
  };

  const statusColors = { received: 'bg-blue-accent/20 text-blue-accent', preparing: 'bg-amber-500/20 text-amber-400', ready: 'bg-emerald-500/20 text-emerald-400', served: 'bg-zinc-500/20 text-zinc-400' };
  const catIcons = { food: ChefHat, drinks: Wine, shisha: Flame };

  // Single-chime alert for managers: plays once when a brand-new untouched
  // order arrives OR when the service worker forwards a service-bell push.
  // As soon as ANY staff (chef/bartender/shisha/manager) advances any item,
  // the order is "acknowledged" and stops counting as pending — so a quick
  // re-glance at the dashboard won't replay the chime. Only fires on the
  // active view; completed orders are history, no chime.
  const { prefs, updatePrefs, playSample } = useOrderAlert({
    role: 'manager',
    orders: view === 'active' ? orders : [],
    getReceivedItems: (o) => {
      const items = o.items || [];
      const allUntouched = items.length > 0 && items.every(i => i.status === 'received');
      return allUntouched ? items : [];
    },
  });

  return (
    <div className="space-y-3 animate-fadeIn">
      <StaleTabsAlert />
      {/* Bartender/manager pending-tabs tray — only renders rows when
          there's something to action so it self-hides when the floor is calm. */}
      {view === 'active' && <PendingTabsTray />}
      {/* Cash drawer pairing card — only renders the unpaired state inline so
          it auto-hides once the till is wired up. Manager-only (the card itself
          shows the WebUSB-unsupported notice if the browser can't talk to USB). */}
      {view === 'active' && canDiscount && <CashDrawerCard compact /> }
      <div className="flex items-center gap-1 mb-1" data-testid="orders-view-tabs">
        <button
          onClick={() => setView('active')}
          data-testid="orders-tab-active"
          className={`px-4 h-9 rounded-sm font-sans text-xs uppercase tracking-[0.18em] border transition-colors ${view === 'active' ? 'bg-gold/15 text-gold border-gold/30' : 'bg-white/[0.02] text-zinc-400 border-white/10 hover:text-zinc-200'}`}
        >Active</button>
        <button
          onClick={() => setView('completed')}
          data-testid="orders-tab-completed"
          className={`px-4 h-9 rounded-sm font-sans text-xs uppercase tracking-[0.18em] border transition-colors ${view === 'completed' ? 'bg-gold/15 text-gold border-gold/30' : 'bg-white/[0.02] text-zinc-400 border-white/10 hover:text-zinc-200'}`}
        >Completed</button>
      </div>
      {view === 'completed' && (
        <div className="flex flex-wrap items-end gap-2 mb-1" data-testid="orders-date-filter">
          <div className="flex flex-col">
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans mb-1">From</Label>
            <Input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="h-9 bg-white/[0.03] border-white/10 text-zinc-200 font-sans text-xs rounded-sm"
              data-testid="orders-from-date"
            />
          </div>
          <div className="flex flex-col">
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans mb-1">To</Label>
            <Input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="h-9 bg-white/[0.03] border-white/10 text-zinc-200 font-sans text-xs rounded-sm"
              data-testid="orders-to-date"
            />
          </div>
          {(fromDate || toDate) && (
            <Button
              onClick={() => { setFromDate(''); setToDate(''); }}
              variant="ghost"
              className="h-9 px-3 text-zinc-400 hover:text-zinc-200 font-sans text-xs uppercase tracking-[0.18em] rounded-sm"
              data-testid="orders-clear-dates"
            >
              Clear · back to last 48h
            </Button>
          )}
          {!fromDate && !toDate && (
            <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans pb-2.5">
              Showing today &amp; yesterday — pick a date to see further back
            </span>
          )}
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <p className="text-sm font-sans text-zinc-400" data-testid="orders-count">
            {orders.length} {view === 'completed' ? 'completed' : 'active'} order{orders.length === 1 ? '' : 's'}
          </p>
          {fetchError && (
            <Badge
              className="bg-red-500/15 text-red-300 border border-red-500/30 font-sans text-[10px] uppercase tracking-[0.18em] animate-pulse"
              data-testid="orders-fetch-error"
              title={fetchError}
            >
              {fetchError.includes('Session') ? 'Logged out' : 'Offline'}
            </Badge>
          )}
          {/* Director-only one-click heal — wipes pre-fix ghost data
              (auto-day-close orders stuck active, voided-empty tabs,
              orphan seated tables). Owner uses this when the live
              orders page still shows old phantom tabs. */}
          {canHeal && view === 'active' && (
            <button
              onClick={healStaleData}
              disabled={healing}
              className="h-7 px-2.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-sm font-sans text-[10px] uppercase tracking-[0.18em] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="heal-stale-data-btn"
              title="Mark auto-day-close ghost orders as completed + close empty tabs + free orphan tables"
            >
              {healing ? 'Healing…' : '⚕ Heal Stale Data'}
            </button>
          )}
        </div>
        {view === 'active' && (
          <>
          <div className="flex items-stretch border border-white/10 rounded-sm overflow-hidden" data-testid="orders-layout-toggle">
            <button
              type="button"
              onClick={() => setActiveLayout('tables')}
              className={`px-3 h-8 text-[11px] uppercase tracking-[0.18em] font-sans transition-colors ${activeLayout === 'tables' ? 'bg-gold/20 text-gold' : 'bg-white/[0.02] text-zinc-400 hover:text-zinc-200'}`}
              data-testid="orders-layout-tables"
              title="Group orders by table (one card per table)"
            >By Table</button>
            <button
              type="button"
              onClick={() => setActiveLayout('list')}
              className={`px-3 h-8 text-[11px] uppercase tracking-[0.18em] font-sans transition-colors border-l border-white/10 ${activeLayout === 'list' ? 'bg-gold/20 text-gold' : 'bg-white/[0.02] text-zinc-400 hover:text-zinc-200'}`}
              data-testid="orders-layout-list"
              title="One card per order (legacy view)"
            >List</button>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={reactivateUnpaid}
            disabled={reactivating}
            className="h-8 border-amber-500/30 text-amber-400 hover:bg-amber-500/10 rounded-sm font-sans text-xs"
            data-testid="orders-reactivate-unpaid"
            title="Move 'completed' orders that aren't actually paid back to Active"
          >
            {reactivating ? 'Working…' : 'Reactivate unpaid'}
          </Button>
          <Button
            size="sm"
            onClick={() => navigate('/serving')}
            className="h-8 bg-blue-accent/10 text-blue-accent border border-blue-accent/20 hover:bg-blue-accent/20 rounded-sm font-sans text-xs"
            data-testid="manager-goto-serving"
            title="Open the Serving Panel to deliver / clear tables"
          >
            <HandPlatter className="w-3 h-3 mr-1.5" /> Serve
          </Button>
          <Button
            size="sm"
            onClick={openTakeOrder}
            className="h-8 bg-gold/10 text-gold border border-gold/20 hover:bg-gold/20 rounded-sm font-sans text-xs"
            data-testid="take-order-button"
            title="Place an order on behalf of a customer at any table"
          >
            <ClipboardPlus className="w-3 h-3 mr-1.5" /> Take Order
          </Button>
          <Button
            size="sm"
            onClick={syncPayments}
            disabled={syncing}
            className="h-8 bg-blue-accent/10 text-blue-accent border border-blue-accent/20 hover:bg-blue-accent/20 rounded-sm font-sans text-xs disabled:opacity-50"
            data-testid="sync-payments-button"
            title="Force-check Square for any pending card payments"
          >
            <RefreshCw className={`w-3 h-3 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync payments'}
          </Button>
          </>
        )}
        <AlertPrefsButton prefs={prefs} onChange={updatePrefs} onTest={playSample} />
      </div>

      {/* Take-Order dialog — table picker */}
      <Dialog open={takeOrderOpen} onOpenChange={setTakeOrderOpen}>
        <DialogContent className="bg-bg-secondary border border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-white">Place Order — Pick a table</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-xs text-zinc-400 font-sans">
              Opens the customer ordering view for the chosen table in a new tab. Payment defaults to <span className="text-gold">cash</span> — change at checkout if needed.
            </p>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Table</Label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[50vh] overflow-y-auto" data-testid="take-order-table-grid">
                {allTables.map(t => {
                  const selected = pickedTableQr === t.qr_code;
                  const occupied = t.status === 'occupied';
                  const reserved = t.status === 'reserved';
                  const badgeCls = occupied
                    ? 'bg-red-500/25 text-red-300'
                    : reserved
                      ? 'bg-blue-accent/20 text-blue-accent'
                      : 'bg-emerald-500/25 text-emerald-300';
                  const borderCls = selected
                    ? 'border-gold ring-2 ring-gold/40'
                    : occupied
                      ? 'border-red-500/40 hover:border-red-400'
                      : reserved
                        ? 'border-blue-accent/30 hover:border-blue-accent'
                        : 'border-emerald-500/30 hover:border-emerald-400';
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setPickedTableQr(t.qr_code)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-sm border bg-white/[0.03] transition-all ${borderCls}`}
                      data-testid={`take-order-table-${t.number}`}
                    >
                      <span className="text-gold font-serif text-2xl font-semibold leading-none">{tableLabel(t.number)}</span>
                      <span className={`px-1.5 py-0.5 rounded-sm font-sans text-[10px] font-bold uppercase tracking-[0.15em] ${badgeCls}`}>{t.status}</span>
                      <span className="text-[10px] text-zinc-500 font-sans">{t.capacity} seats</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <Button
              onClick={launchStaffOrder}
              className="w-full bg-gold/20 text-gold border border-gold/30 hover:bg-gold/30 rounded-sm font-sans"
              data-testid="confirm-take-order-button"
            >Open Ordering View →</Button>
          </div>
        </DialogContent>
      </Dialog>
      {orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Clock className="w-10 h-10 text-zinc-700 mb-3" />
          <p className="text-zinc-500 font-sans" data-testid="no-orders">
            {view === 'completed' ? 'No completed orders yet' : 'No active orders'}
          </p>
        </div>
      ) : view === 'active' && activeLayout === 'tables' ? (
        // ---------- BY-TABLE VIEW ----------
        // Group every active order by table_id. Each card summarises the
        // whole table — total, count, payment statuses, customer names —
        // and expands to show the line items. When ALL orders on a table
        // are paid (or tab settled) + served, the table disappears from
        // Active and shows up in Completed at the bottom of the page.
        (() => {
          const groups = {};
          for (const o of orders) {
            // Uber Eats orders get their own always-on-top group — they have
            // a ~11min accept SLA and must never hide inside "T?".
            const isUber = o.order_type === 'uber_eats';
            const key = isUber ? 'uber-eats' : (o.table_id || 'no-table');
            if (!groups[key]) {
              groups[key] = { table_id: isUber ? 'uber-eats' : o.table_id, table_number: o.table_number, is_uber: isUber, orders: [] };
            }
            groups[key].orders.push(o);
          }
          const tables = Object.values(groups).sort((a, b) => ((b.is_uber ? 1 : 0) - (a.is_uber ? 1 : 0)) || ((a.table_number || 0) - (b.table_number || 0)));
          const allCollapsed = tables.length > 0 && tables.every(t => collapsedTables.has(t.table_id));
          return (
            <div className="space-y-2" data-testid="orders-by-table-wrapper">
              {tables.length > 1 && (
                <div className="flex items-center justify-between px-1">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-sans">{tables.length} tables · all open by default</span>
                  <button
                    type="button"
                    onClick={() => {
                      const next = allCollapsed ? new Set() : new Set(tables.map(t => t.table_id));
                      setCollapsedTables(next);
                      try { localStorage.setItem('majnu:orders:collapsedTables', JSON.stringify(Array.from(next))); } catch { /* sandboxed */ }
                    }}
                    className="text-[11px] text-zinc-400 hover:text-white font-sans uppercase tracking-[0.1em]"
                    data-testid="orders-toggle-all-tables"
                  >
                    {allCollapsed ? 'Expand all' : 'Collapse all'}
                  </button>
                </div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="orders-by-table-grid">
              {tables.map(grp => {
                const total = grp.orders.reduce((s, o) => s + (o.total || 0), 0);
                const counts = grp.orders.length;
                const hasUnpaid = grp.orders.some(o => o.payment_status === 'pending_cash' || o.payment_status === 'pending');
                const hasTab = grp.orders.some(o => o.payment_status === 'tab');
                const allReady = grp.orders.every(o => o.items.every(i => i.status === 'ready' || i.status === 'served'));
                const customerNames = [...new Set(grp.orders.map(o => o.customer_name).filter(Boolean))];
                const isExpanded = !collapsedTables.has(grp.table_id);
                const pendingUber = grp.is_uber ? grp.orders.filter(o => o.uber_status === 'pending_acceptance').length : 0;
                return (
                  <div key={grp.table_id} className={`rounded-sm border ${grp.is_uber ? (pendingUber > 0 ? 'bg-emerald-500/[0.05] border-emerald-500/40' : 'bg-emerald-500/[0.02] border-emerald-500/15') : 'bg-white/[0.03] border-white/5'}`} data-testid={`table-group-${grp.table_id}`}>
                    {/* Header — clickable summary */}
                    <button
                      type="button"
                      onClick={() => toggleTableCollapsed(grp.table_id)}
                      className="w-full flex items-center justify-between gap-3 p-3 hover:bg-white/[0.02] transition-colors text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-12 h-12 rounded-sm border flex items-center justify-center shrink-0 ${grp.is_uber ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-gold/10 border-gold/30'}`}>
                          <span className={`font-serif font-semibold leading-none ${grp.is_uber ? 'text-emerald-400 text-sm' : 'text-gold text-lg'}`}>{grp.is_uber ? 'UBER' : tableLabel(grp.table_number)}</span>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-white font-sans text-sm font-semibold">{counts} order{counts === 1 ? '' : 's'}</span>
                            {pendingUber > 0 && (
                              <Badge className="bg-emerald-500/25 text-emerald-300 border-0 font-sans text-[10px] animate-pulse" data-testid="uber-pending-count">{pendingUber} TO ACCEPT</Badge>
                            )}
                            {hasTab && <Badge className="bg-blue-accent/15 text-blue-accent border-0 font-sans text-[10px] uppercase">Tab open</Badge>}
                            {hasUnpaid && <Badge className="bg-amber-500/15 text-amber-400 border-0 font-sans text-[10px] uppercase">Unpaid</Badge>}
                            {!hasUnpaid && !hasTab && <Badge className="bg-emerald-500/15 text-emerald-400 border-0 font-sans text-[10px] uppercase">Paid</Badge>}
                            {allReady && <Badge className="bg-emerald-500/15 text-emerald-400 border-0 font-sans text-[10px] uppercase">Ready</Badge>}
                          </div>
                          {customerNames.length > 0 && (
                            <p className="text-[11px] text-zinc-500 font-sans truncate mt-0.5">{customerNames.join(', ')}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="text-gold font-serif font-semibold text-lg block leading-none">${total.toFixed(2)}</span>
                        <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-sans">{isExpanded ? 'Hide' : 'View'}</span>
                      </div>
                    </button>
                    {/* Expanded — individual order cards. Paid tab orders
                        get bundled under a SettledTabGroupCard inside the
                        table so they stay visually grouped per tab. */}
                    {isExpanded && (() => {
                      const tabBuckets = {};
                      const directOrders = [];
                      for (const o of grp.orders) {
                        // Tab order is "settled" only after the WHOLE tab has been
                        // closed at the bar — i.e. backend stamped `tab_settled_at`
                        // alongside payment_status='paid'. Just collecting the $30
                        // deposit (tab.deposit_status='paid') leaves the orders as
                        // payment_status='tab' so they stay actionable. Also guards
                        // against orphan/test data with payment_status='paid' but
                        // no settlement timestamp.
                        const isSettledTabOrder =
                          o.tab_id &&
                          o.payment_method === 'tab' &&
                          (o.payment_status === 'paid' || o.payment_status === 'refunded') &&
                          !!o.tab_settled_at;
                        if (isSettledTabOrder) {
                          if (!tabBuckets[o.tab_id]) tabBuckets[o.tab_id] = [];
                          tabBuckets[o.tab_id].push(o);
                        } else {
                          directOrders.push(o);
                        }
                      }
                      const tabEntries = Object.entries(tabBuckets);
                      return (
                      <div className="border-t border-white/5 p-3 space-y-2" data-testid={`table-group-${grp.table_id}-orders`}>
                        {tabEntries.length > 0 && (
                          <div className="space-y-2">
                            {tabEntries.map(([tabId, tabOrders]) => (
                              <SettledTabGroupCard key={tabId} tabId={tabId} orders={tabOrders} />
                            ))}
                          </div>
                        )}
                        {directOrders.map(order => {
                          const elapsed = getElapsedMinutes(order.created_at);
                          const maxDelay = Math.max(...order.items.map(i => delayThresholds[i.category] || 10));
                          const isDelayed = elapsed > maxDelay;
                          return (
                            <div key={order.id} className={`rounded-sm border p-3 ${isDelayed ? 'border-red-500/30 bg-red-500/[0.04]' : 'border-white/8 bg-black/30'}`} data-testid={`manager-order-${order.id}`}>
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <span className="text-zinc-500 text-[11px] font-sans">#{order.id.slice(0, 8)}</span>
                                  {order.order_type === 'uber_eats' && (
                                    <Badge className={`ml-1.5 border-0 font-sans text-[10px] ${order.uber_status === 'pending_acceptance' ? 'bg-emerald-500/20 text-emerald-300 animate-pulse' : 'bg-emerald-500/10 text-emerald-500'}`} data-testid={`uber-badge-${order.id}`}>
                                      {order.uber_status === 'pending_acceptance' ? 'UBER · ACCEPT NOW' : `UBER · ${(order.uber_status || '').toUpperCase()}`}
                                    </Badge>
                                  )}
                                  <p className="text-xs text-zinc-400 font-sans">{order.customer_name}</p>
                                </div>
                                <div className="text-right">
                                  {isDelayed && <AlertTriangle className="w-3.5 h-3.5 text-red-400 inline mr-1" />}
                                  <span className={`font-mono text-xs ${isDelayed ? 'text-red-400' : elapsed > maxDelay * 0.7 ? 'text-amber-400' : 'text-emerald-400'}`}>{elapsed}m</span>
                                  <Badge className={`ml-2 ${order.payment_status === 'paid' || order.payment_status === 'tab' ? 'bg-emerald-500/15 text-emerald-400' : order.payment_status === 'pending_cash' ? 'bg-amber-500/15 text-amber-400' : 'bg-zinc-500/15 text-zinc-400'} border-0 font-sans text-[10px]`}>{order.payment_method}</Badge>
                                </div>
                              </div>
                              <div className="space-y-1 mb-2">
                                {order.items.map(item => {
                                  const CatIcon = catIcons[item.category] || UtensilsCrossed;
                                  // Owner request 2026-02: show per-line pricing
                                  // (`unit × qty = total`) so staff can see what's
                                  // being charged for what at a glance.
                                  const unit = Number(item.price ?? 0);
                                  const lineTotal = Number(item.total ?? unit * item.quantity);
                                  return (
                                    <div key={item.id} className="flex items-center justify-between gap-2 text-xs">
                                      <span className="text-zinc-300 font-sans flex items-center gap-1.5 min-w-0">
                                        <CatIcon className="w-3 h-3 text-zinc-600 shrink-0" />
                                        <span className="truncate">{item.quantity}x {item.name}</span>
                                      </span>
                                      <span className="flex items-center gap-2 shrink-0">
                                        <span className="text-zinc-500 font-mono text-[11px]">
                                          ${unit.toFixed(2)}
                                          {item.quantity > 1 && <span className="text-zinc-600"> = ${lineTotal.toFixed(2)}</span>}
                                        </span>
                                        <Badge className={`${statusColors[item.status]} border-0 font-sans text-[10px]`}>{item.status}</Badge>
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="flex flex-wrap items-center justify-between gap-y-1 gap-x-2 pt-2 border-t border-white/5">
                                {/* Discount line — only renders when a discount
                                    is active OR when there's a subtotal different
                                    from total. Keeps the row noise-free for the
                                    99% of orders with no discount. */}
                                <div className="flex flex-col">
                                  {order.discount_amount > 0 && (
                                    <span className="text-[10px] text-zinc-500 font-sans">
                                      Subtotal ${Number(order.subtotal ?? order.total).toFixed(2)} · <span className="text-pink-300">{order.discount_label || 'Discount'} −${Number(order.discount_amount).toFixed(2)}</span>
                                    </span>
                                  )}
                                  <span className="text-gold font-sans font-semibold text-sm">${order.total.toFixed(2)}</span>
                                </div>
                                <div className="flex flex-wrap gap-1 justify-end">
                                  {order.order_type === 'uber_eats' && order.uber_status === 'pending_acceptance' && (
                                    <>
                                      <Button size="sm" onClick={() => acceptUberOrder(order.id)} className="h-7 bg-emerald-500 text-black font-bold border border-emerald-400 hover:bg-emerald-400 rounded-sm font-sans text-xs px-2" data-testid={`uber-accept-${order.id}`}>
                                        Accept Uber
                                      </Button>
                                      <Button size="sm" onClick={() => denyUberOrder(order.id)} className="h-7 bg-red-500/10 text-red-300 border border-red-500/30 hover:bg-red-500/20 rounded-sm font-sans text-xs px-2" data-testid={`uber-reject-${order.id}`}>
                                        Reject
                                      </Button>
                                    </>
                                  )}
                                  <ReceiptPrint orderId={order.id} />
                                  {canDiscount && (
                                    <OrderDiscountButton order={order} onApplied={fetchOrders} />
                                  )}
                                  {/* "Cash OK" approval button removed Feb
                                      2026 — the pending-cash workflow is
                                      retired. Backend status string left in
                                      place so historical rows render safely. */}
                                  {order.payment_status === 'tab' && (
                                    <Button size="sm" onClick={() => openSettleTab(order.id)} className="h-7 bg-blue-accent/15 text-blue-accent border border-blue-accent/30 hover:bg-blue-accent/25 rounded-sm font-sans text-xs px-2" data-testid={`settle-tab-${order.id}`}>
                                      <Receipt className="w-3 h-3 mr-1" /> Settle Tab
                                    </Button>
                                  )}
                                  {/* Void Tab — admin/manager god-mode. Wipes
                                      every order on this tab + closes the tab.
                                      Frees the table if nothing else is on it. */}
                                  {order.payment_status === 'tab' && canDiscount && (
                                    <Button size="sm" onClick={() => setVoidTabDialog({
                                      tabId: order.tab_id,
                                      customerName: order.customer_name,
                                      tableNumber: order.table_number,
                                    })} className="h-7 bg-red-500/10 text-red-300 border border-red-500/30 hover:bg-red-500/20 rounded-sm font-sans text-xs px-2" data-testid={`void-tab-${order.tab_id || order.id}`}>
                                      <Ban className="w-3 h-3 mr-1" /> Void Tab
                                    </Button>
                                  )}
                                  {/* Manual card payment (EFTPOS off-band) — any unpaid active order. */}
                                  {order.payment_status !== 'paid' && order.payment_status !== 'refunded' && order.payment_status !== 'tab' && (
                                    <Button size="sm" onClick={() => markPaidByCard(order.id)} className="h-7 bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/20 rounded-sm font-sans text-xs px-2" data-testid={`mark-paid-card-${order.id}`}>
                                      <CreditCard className="w-3 h-3 mr-1" /> Paid (Card)
                                    </Button>
                                  )}
                                  {/* Split payment — open the partial-payments dialog so the
                                      manager can record Cash $X + Card $Y + EFTPOS $Z. */}
                                  {order.payment_status !== 'paid' && order.payment_status !== 'refunded' && order.payment_status !== 'tab' && (
                                    <Button size="sm" onClick={() => setSplitOrderId(order.id)} className="h-7 bg-gold/10 text-gold border border-gold/30 hover:bg-gold/20 rounded-sm font-sans text-xs px-2" data-testid={`split-payment-${order.id}`}>
                                      <Wallet className="w-3 h-3 mr-1" /> Split
                                    </Button>
                                  )}
                                  <Button size="sm" onClick={() => setEditOrder(order)} className="h-7 bg-blue-accent/10 text-blue-accent border border-blue-accent/20 hover:bg-blue-accent/20 rounded-sm font-sans text-xs px-2" data-testid={`edit-order-${order.id}`}>
                                    <Pencil className="w-3 h-3 mr-1" /> Edit
                                  </Button>
                                  <Dialog open={voidDialog === order.id} onOpenChange={open => { if (!open) setVoidDialog(null); }}>
                                    <DialogTrigger asChild>
                                      <Button size="sm" variant="ghost" onClick={() => setVoidDialog(order.id)} className="h-7 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm font-sans text-xs px-2" data-testid={`void-order-${order.id}`}>
                                        <Ban className="w-3 h-3 mr-1" /> Void
                                      </Button>
                                    </DialogTrigger>
                                    <DialogContent className="bg-bg-secondary border border-white/10">
                                      <DialogHeader><DialogTitle className="font-serif text-white">Void Order #{order.id.slice(0, 8)}</DialogTitle></DialogHeader>
                                      <div className="space-y-4 pt-2">
                                        <div className="space-y-2">
                                          <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Reason</Label>
                                          <Input value={voidReason} onChange={e => setVoidReason(e.target.value)} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Enter reason" data-testid="void-reason-input" />
                                        </div>
                                        <Button onClick={voidOrder} className="w-full bg-red-500/20 text-red-400 border border-red-500/20 hover:bg-red-500/30 rounded-sm font-sans" data-testid="confirm-void-button">Confirm Void</Button>
                                      </div>
                                    </DialogContent>
                                  </Dialog>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
            </div>
          );
        })()
      ) : (
        // ---------- LIST / COMPLETED VIEW ----------
        // Settled-tab orders (payment_status='paid' + payment_method='tab') are
        // grouped under one collapsible "Tab" card per owner request: tab
        // payments stay grouped even after payment. Direct orders render as
        // individual cards as before. Applies to both Active (list mode) and
        // Completed view.
        (() => {
          const tabGroups = {};
          const flatOrders = [];
          for (const o of orders) {
            // Same defensive rule as the table-grouped view: a tab order is
            // only "settled" after the backend stamps tab_settled_at (i.e. the
            // full tab was closed). Deposit-only confirmations leave
            // payment_status='tab', and orphan paid-without-settlement rows
            // are treated as still-active so staff can still take action.
            const isSettledTabOrder =
              o.tab_id &&
              o.payment_method === 'tab' &&
              (o.payment_status === 'paid' || o.payment_status === 'refunded') &&
              !!o.tab_settled_at;
            if (isSettledTabOrder) {
              if (!tabGroups[o.tab_id]) tabGroups[o.tab_id] = [];
              tabGroups[o.tab_id].push(o);
            } else {
              flatOrders.push(o);
            }
          }
          // Sort tab groups by most-recent settle time so newest tabs land at the top.
          const groupEntries = Object.entries(tabGroups).sort((a, b) => {
            const aSettled = a[1].map(o => o.tab_settled_at || o.updated_at).sort().pop() || '';
            const bSettled = b[1].map(o => o.tab_settled_at || o.updated_at).sort().pop() || '';
            return bSettled.localeCompare(aSettled);
          });
          return (
            <>
              {groupEntries.length > 0 && (
                <div className="space-y-3 mb-3" data-testid="settled-tab-groups">
                  {groupEntries.map(([tabId, groupOrders]) => (
                    <SettledTabGroupCard key={tabId} tabId={tabId} orders={groupOrders} />
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
                {flatOrders.map(order => {
            const elapsed = getElapsedMinutes(order.created_at);
            const maxDelay = Math.max(...order.items.map(i => delayThresholds[i.category] || 10));
            const isDelayed = view === 'active' && elapsed > maxDelay;
            const completedAt = order.completed_at || order.updated_at;
            return (
              <div key={order.id} className={`bg-white/[0.03] border rounded-sm p-4 ${isDelayed ? 'border-red-500/30 pulse-gold' : view === 'completed' ? 'border-emerald-500/15' : 'border-white/5'}`} data-testid={`manager-order-${order.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <span className="text-gold font-serif text-xl font-semibold">{order.order_type === 'uber_eats' ? 'Uber Eats' : tableLabel(order.table_number)}</span>
                    <span className="text-xs text-zinc-600 font-sans ml-2">#{order.id.slice(0, 8)}</span>
                    {order.order_type === 'uber_eats' && (
                      <Badge className={`ml-2 border-0 font-sans text-[10px] ${order.uber_status === 'pending_acceptance' ? 'bg-emerald-500/20 text-emerald-300 animate-pulse' : 'bg-emerald-500/10 text-emerald-500'}`} data-testid={`uber-badge-${order.id}`}>
                        {order.uber_status === 'pending_acceptance' ? 'UBER · ACCEPT NOW' : `UBER · ${(order.uber_status || '').toUpperCase()}`}
                      </Badge>
                    )}
                    <p className="text-xs text-zinc-500 font-sans">{order.customer_name}</p>
                  </div>
                  <div className="text-right">
                    {view === 'completed' ? (
                      <span className="font-mono text-xs text-emerald-400" title={completedAt}>
                        ✓ {completedAt ? new Date(completedAt).toLocaleString('en-AU', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }) : 'done'}
                      </span>
                    ) : (
                      <>
                        {isDelayed && <AlertTriangle className="w-4 h-4 text-red-400 inline mr-1" />}
                        <span className={`font-mono text-sm ${isDelayed ? 'text-red-400' : elapsed > maxDelay * 0.7 ? 'text-amber-400' : 'text-emerald-400'}`}>
                          {elapsed}m
                        </span>
                      </>
                    )}
                    <Badge className={`ml-2 ${order.payment_status === 'paid' || order.payment_status === 'tab' ? 'bg-emerald-500/20 text-emerald-400' : order.payment_status === 'pending_cash' ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-500/20 text-zinc-400'} border-0 font-sans text-xs`}>
                      {order.payment_method}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-1 mb-3">
                  {order.items.map(item => {
                    const CatIcon = catIcons[item.category] || UtensilsCrossed;
                    const unit = Number(item.price ?? 0);
                    const lineTotal = Number(item.total ?? unit * item.quantity);
                    return (
                      <div key={item.id} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-zinc-300 font-sans flex items-center gap-1.5 min-w-0">
                          <CatIcon className="w-3 h-3 text-zinc-600 shrink-0" />
                          <span className="truncate">{item.quantity}x {item.name}</span>
                        </span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="text-zinc-500 font-mono text-xs">
                            ${unit.toFixed(2)}
                            {item.quantity > 1 && <span className="text-zinc-600"> = ${lineTotal.toFixed(2)}</span>}
                          </span>
                          <Badge className={`${statusColors[item.status]} border-0 font-sans text-xs`}>{item.status}</Badge>
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-3 pt-2 border-t border-white/5">
                  <div className="flex flex-col">
                    {order.discount_amount > 0 && (
                      <span className="text-[10px] text-zinc-500 font-sans">
                        Subtotal ${Number(order.subtotal ?? order.total).toFixed(2)} · <span className="text-pink-300">{order.discount_label || 'Discount'} −${Number(order.discount_amount).toFixed(2)}</span>
                      </span>
                    )}
                    <span className="text-gold font-sans font-semibold">${order.total.toFixed(2)}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 justify-end min-w-0">
                    {view === 'active' && order.order_type === 'uber_eats' && order.uber_status === 'pending_acceptance' && (
                      <>
                        <Button size="sm" onClick={() => acceptUberOrder(order.id)} className="h-7 bg-emerald-500 text-black font-bold border border-emerald-400 hover:bg-emerald-400 rounded-sm font-sans text-xs px-2" data-testid={`uber-accept-${order.id}`}>
                          Accept Uber
                        </Button>
                        <Button size="sm" onClick={() => denyUberOrder(order.id)} className="h-7 bg-red-500/10 text-red-300 border border-red-500/30 hover:bg-red-500/20 rounded-sm font-sans text-xs px-2" data-testid={`uber-reject-${order.id}`}>
                          Reject
                        </Button>
                      </>
                    )}
                    <ReceiptPrint orderId={order.id} />
                    {view === 'active' && canDiscount && (
                      <OrderDiscountButton order={order} onApplied={fetchOrders} />
                    )}
                    {/* "Cash OK" approval button removed Feb 2026 — see
                        sibling note above. Pending-cash flow retired. */}
                    {view === 'active' && order.payment_status === 'tab' && (
                      <Button size="sm" onClick={() => openSettleTab(order.id)} className="h-7 bg-blue-accent/15 text-blue-accent border border-blue-accent/30 hover:bg-blue-accent/25 rounded-sm font-sans text-xs px-2" data-testid={`settle-tab-${order.id}`}>
                        <Receipt className="w-3 h-3 mr-1" /> Settle Tab
                      </Button>
                    )}
                    {/* Void Tab — admin/manager only. */}
                    {view === 'active' && order.payment_status === 'tab' && canDiscount && (
                      <Button size="sm" onClick={() => setVoidTabDialog({
                        tabId: order.tab_id,
                        customerName: order.customer_name,
                        tableNumber: order.table_number,
                      })} className="h-7 bg-red-500/10 text-red-300 border border-red-500/30 hover:bg-red-500/20 rounded-sm font-sans text-xs px-2" data-testid={`void-tab-list-${order.tab_id || order.id}`}>
                        <Ban className="w-3 h-3 mr-1" /> Void Tab
                      </Button>
                    )}
                    {/* Manual card payment (EFTPOS off-band) — any unpaid active order. */}
                    {view === 'active' && order.payment_status !== 'paid' && order.payment_status !== 'refunded' && order.payment_status !== 'tab' && (
                      <Button size="sm" onClick={() => markPaidByCard(order.id)} className="h-7 bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/20 rounded-sm font-sans text-xs px-2" data-testid={`mark-paid-card-${order.id}`}>
                        <CreditCard className="w-3 h-3 mr-1" /> Paid (Card)
                      </Button>
                    )}
                    {view === 'active' && order.payment_status !== 'paid' && order.payment_status !== 'refunded' && order.payment_status !== 'tab' && (
                      <Button size="sm" onClick={() => setSplitOrderId(order.id)} className="h-7 bg-gold/10 text-gold border border-gold/30 hover:bg-gold/20 rounded-sm font-sans text-xs px-2" data-testid={`split-payment-${order.id}`}>
                        <Wallet className="w-3 h-3 mr-1" /> Split
                      </Button>
                    )}
                    {view === 'active' && (
                      <Button size="sm" onClick={() => setEditOrder(order)} className="h-7 bg-blue-accent/10 text-blue-accent border border-blue-accent/20 hover:bg-blue-accent/20 rounded-sm font-sans text-xs px-2" data-testid={`edit-order-${order.id}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Edit
                      </Button>
                    )}
                    {view === 'active' && (
                    <Dialog open={voidDialog === order.id} onOpenChange={open => { if (!open) setVoidDialog(null); }}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="ghost" onClick={() => setVoidDialog(order.id)} className="h-7 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-sm font-sans text-xs px-2" data-testid={`void-order-${order.id}`}>
                          <Ban className="w-3 h-3 mr-1" /> Void
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-bg-secondary border border-white/10">
                        <DialogHeader><DialogTitle className="font-serif text-white">Void Order #{order.id.slice(0, 8)}</DialogTitle></DialogHeader>
                        <div className="space-y-4 pt-2">
                          <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Reason</Label>
                            <Input value={voidReason} onChange={e => setVoidReason(e.target.value)} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Enter reason" data-testid="void-reason-input" />
                          </div>
                          <Button onClick={voidOrder} className="w-full bg-red-500/20 text-red-400 border border-red-500/20 hover:bg-red-500/30 rounded-sm font-sans" data-testid="confirm-void-button">Confirm Void</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
              </div>
            </>
          );
        })()
      )}
      <OrderEditDialog
        open={!!editOrder}
        onOpenChange={(o) => !o && setEditOrder(null)}
        order={editOrder}
        actorRole="manager"
        onSaved={() => { setEditOrder(null); fetchOrders(); }}
      />

      {/* Settle Tab dialog — appears when manager clicks Settle on a tab
          order. Shows the per-order breakdown, the $30 upfront credit, and
          the final amount to collect at the counter. */}
      <Dialog open={!!settlePreview} onOpenChange={(o) => { if (!o) { setSettlePreview(null); setShowPartials(false); } }}>
        <DialogContent className="bg-bg-secondary border border-white/10 max-w-md" data-testid="settle-tab-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-white flex items-center gap-2">
              <Receipt className="w-4 h-4 text-blue-accent" />
              Settle Tab
            </DialogTitle>
          </DialogHeader>
          {settlePreview && (
            <div className="space-y-4 pt-2">
              <div className="rounded-sm border border-white/10 bg-white/[0.03] p-3 space-y-1.5 max-h-48 overflow-y-auto">
                <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-sans mb-1">Tab orders ({settlePreview.orders.length})</p>
                {settlePreview.orders.map(o => (
                  <div key={o.id} className="flex items-center justify-between font-sans text-sm">
                    <span className="text-zinc-300 truncate">#{o.id.slice(0, 6)} · {tableLabel(o.table_number)} · {o.items?.length || 0} items</span>
                    <span className="text-white font-mono">${Number(o.total || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="rounded-sm border border-white/10 bg-white/[0.03] p-3 space-y-1.5">
                <div className="flex justify-between font-sans text-sm">
                  <span className="text-zinc-400">Tab total</span>
                  <span className="text-white font-mono" data-testid="settle-sum-total">${Number(settlePreview.sum_total).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-sans text-sm">
                  <span className="text-zinc-400">Less upfront deposit <span className="text-zinc-500">({settlePreview.tab?.deposit_method === 'card' ? 'card' : settlePreview.tab?.deposit_method === 'apple_pay' ? 'Apple Pay' : 'cash'})</span></span>
                  <span className="text-emerald-400 font-mono">−${Number(settlePreview.upfront_amount).toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-white/10 mt-2">
                  <span className="text-white font-medium">Owed at bar</span>
                  <span className="text-gold font-bold font-mono text-lg" data-testid="settle-owed">${Number(settlePreview.owed_at_counter).toFixed(2)}</span>
                </div>
                {settlePreview.discount_eligible_for_cash && (
                  <div className="mt-2 pt-2 border-t border-emerald-500/30 space-y-1">
                    <div className="flex justify-between font-sans text-xs">
                      <span className="text-emerald-300 font-semibold uppercase tracking-[0.08em]">If Settled Cash · 10% Off</span>
                      <span className="text-emerald-300 font-mono">−${Number(settlePreview.cash_discount_amount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-sans text-sm">
                      <span className="text-white font-medium">Cash owed</span>
                      <span className="text-emerald-300 font-bold font-mono text-base" data-testid="settle-cash-owed">${Number(settlePreview.cash_owed_at_counter).toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
              {Number(settlePreview.owed_at_counter) <= 0 && (
                <p className="text-xs text-zinc-400 font-sans bg-amber-500/10 border border-amber-500/30 rounded-sm p-2.5">
                  Tab spend below the $30 minimum — the upfront covers the bill, nothing to collect.
                </p>
              )}
              <p className="text-xs text-zinc-400 font-sans leading-relaxed">
                {settlePreview.discount_eligible_for_cash
                  ? <>This tab was opened with <strong className="text-emerald-300">Cash Pay On Bar</strong>. The customer earns <strong className="text-emerald-300">10% off</strong> when they settle in cash. Pick the method you collected with:</>
                  : <>Confirm once you've collected the amount at the bar (cash or EFTPOS). The tab will close and all {settlePreview.orders.length} order{settlePreview.orders.length === 1 ? '' : 's'} will be marked paid.</>}
              </p>
              {settlePreview.discount_eligible_for_cash ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Button
                    onClick={() => confirmSettleTab('cash')}
                    disabled={settling}
                    className="h-11 bg-emerald-500/20 border border-emerald-500/60 text-emerald-200 hover:bg-emerald-500/30 font-bold tracking-[0.05em] uppercase text-xs rounded-sm font-sans disabled:opacity-50"
                    data-testid="confirm-settle-tab-cash"
                  >
                    {settling ? 'Closing…' : <>Settle Cash · 10% Off · ${Number(settlePreview.cash_owed_at_counter).toFixed(2)}</>}
                  </Button>
                  <Button
                    onClick={() => confirmSettleTab('card')}
                    disabled={settling}
                    className="h-11 bg-blue-accent/15 border border-blue-accent/50 text-blue-accent hover:bg-blue-accent/25 font-bold tracking-[0.05em] uppercase text-xs rounded-sm font-sans disabled:opacity-50"
                    data-testid="confirm-settle-tab-card"
                  >
                    {settling ? 'Closing…' : <>Settle Card · ${Number(settlePreview.owed_at_counter).toFixed(2)}</>}
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => confirmSettleTab(null)}
                  disabled={settling}
                  className="w-full h-11 bg-gold text-black font-bold tracking-[0.1em] uppercase text-sm rounded-sm hover:bg-gold-light font-sans disabled:opacity-50"
                  data-testid="confirm-settle-tab"
                >
                  {settling ? 'Closing tab…' : Number(settlePreview.owed_at_counter) > 0 ? `Collected $${Number(settlePreview.owed_at_counter).toFixed(2)} — close tab` : 'Close tab — $0 to collect'}
                </Button>
              )}
              {/* Partial Payments — manager can split the tab across Cash /
                  Card / EFTPOS. Hidden behind a toggle so the existing
                  one-tap full-settle buttons stay the fast default. */}
              {settlePreview.tab?.id && Number(settlePreview.owed_at_counter) > 0 && (
                <div className="pt-2 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => setShowPartials(s => !s)}
                    className="w-full text-left text-[10px] uppercase tracking-[0.18em] text-zinc-400 hover:text-gold font-sans font-bold py-1.5"
                    data-testid="toggle-partials"
                  >
                    {showPartials ? '▾ Hide Split Payment' : '▸ Take Partial Payments (Split Cash · Card · EFTPOS)'}
                  </button>
                  {showPartials && (
                    <PartialPaymentsPanel
                      targetType="tab"
                      targetId={settlePreview.tab.id}
                      actorRole={user?.role}
                      onSettled={() => {
                        setSettlePreview(null);
                        setShowPartials(false);
                        fetchOrders();
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Split Payment dialog — direct (non-tab) order. Manager records
          Cash $X + Card $Y + EFTPOS $Z partials until the order is paid. */}
      <Dialog open={!!splitOrderId} onOpenChange={(o) => { if (!o) setSplitOrderId(null); }}>
        <DialogContent className="bg-bg-secondary border border-white/10 max-w-md" data-testid="split-payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-white flex items-center gap-2">
              <Wallet className="w-4 h-4 text-gold" />
              Split Payment · Order #{(splitOrderId || '').slice(0, 6).toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          {splitOrderId && (
            <div className="pt-2">
              <PartialPaymentsPanel
                targetType="order"
                targetId={splitOrderId}
                actorRole={user?.role}
                onSettled={() => {
                  setSplitOrderId(null);
                  fetchOrders();
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Post-settle "Mark Available / Keep Occupied" prompt */}
      <PostSettleTablePrompt
        open={!!postSettlePromptTable}
        table={postSettlePromptTable}
        onClose={() => setPostSettlePromptTable(null)}
        onResolved={fetchOrders}
      />

      {/* Void Entire Tab — admin/manager god-mode. Wipes every order on
          the tab + closes the tab + frees the table if nothing else
          remains. Refund (if customer prepaid) is flagged for follow-up. */}
      <Dialog open={!!voidTabDialog} onOpenChange={(o) => { if (!o) { setVoidTabDialog(null); setVoidTabReason(''); } }}>
        <DialogContent className="bg-bg-secondary border border-red-500/30 max-w-md" data-testid="void-tab-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-white flex items-center gap-2">
              <Ban className="w-4 h-4 text-red-400" />
              Void Entire Tab
            </DialogTitle>
          </DialogHeader>
          {voidTabDialog && (
            <div className="space-y-4 pt-2">
              <div className="bg-red-500/[0.04] border border-red-500/20 rounded-sm p-3 space-y-1">
                <p className="text-xs text-red-200 font-sans uppercase tracking-[0.12em]">This will:</p>
                <ul className="text-[12px] text-zinc-300 font-sans list-disc list-inside space-y-0.5">
                  <li>Void every order on this tab</li>
                  <li>Close the tab (cannot be reopened)</li>
                  <li>Free the table if nothing else is on it</li>
                  <li>Flag any prepaid amount as <span className="text-amber-300">refund due</span></li>
                </ul>
                <div className="pt-1 text-[11px] text-zinc-500 font-sans">
                  Customer: <span className="text-white">{voidTabDialog.customerName || '—'}</span>
                  {voidTabDialog.tableNumber ? <> · Table <span className="text-gold">{tableLabel(voidTabDialog.tableNumber)}</span></> : null}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Reason (required)</Label>
                <Input
                  value={voidTabReason}
                  onChange={e => setVoidTabReason(e.target.value)}
                  className="bg-transparent border border-white/10 text-white font-sans"
                  placeholder="e.g. customer walked out / duplicate tab"
                  data-testid="void-tab-reason-input"
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  onClick={() => { setVoidTabDialog(null); setVoidTabReason(''); }}
                  className="flex-1 text-zinc-400 hover:text-white"
                  data-testid="void-tab-cancel"
                >Cancel</Button>
                <Button
                  onClick={voidTab}
                  className="flex-1 bg-red-500/20 text-red-300 border border-red-500/30 hover:bg-red-500/30 rounded-sm font-sans"
                  data-testid="void-tab-confirm"
                >Confirm Void</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
